import React, { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import Spinner from '@components/Spinner';
import OrgChartsGridItem from '@components/pages/OrgCharts/GridItem';
import gridStyles from '@components/pages/OrgCharts/styles.module.scss';
import useEventTracking from '@hooks/useEventTracking';
import useRecentlyUpdatedOrgCharts from '@hooks/useRecentlyUpdatedOrgCharts';
import { org_charts_path } from '@routes';
import { ClickTarget, TrackingLocation, click, impression } from '@tracking';

import ForYouSection from './Section';
import { FOR_YOU_ENGAGEMENT_LABELS, FOR_YOU_SECTION_IDS, VIEW_ALL_ORG_CHARTS } from './constants';
import useRegisterSection from './useRegisterSection';

const ForYouOrgCharts = () => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.OrgChartMoreOrgChartsGrid,
  });

  const { orgCharts, isLoading } = useRecentlyUpdatedOrgCharts();

  const orgChartsDisplayText = useMemo(() => {
    if (!orgCharts) return FOR_YOU_ENGAGEMENT_LABELS.NotEngaged.Pro.OrgCharts;
    return FOR_YOU_ENGAGEMENT_LABELS.Engaged.Pro.OrgCharts;
  }, [orgCharts]);

  useRegisterSection(FOR_YOU_SECTION_IDS.OrgCharts, isLoading || !!orgCharts);

  if (!isLoading && !orgCharts) return null;

  const handleViewAllClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.AnchorLink, clickText: VIEW_ALL_ORG_CHARTS }));
  };

  return (
    <ForYouSection
      id={FOR_YOU_SECTION_IDS.OrgCharts}
      highlight={orgChartsDisplayText.label}
      subtitle={orgChartsDisplayText.subtitle}
      title={orgChartsDisplayText.title}
    >
      <Track>
        <InView
          onChange={(inView) => {
            inView && trackEvent(impression({ impressionElement: FOR_YOU_SECTION_IDS.OrgCharts }));
          }}
          triggerOnce
        >
          {!isLoading && orgCharts ? (
            <div className="mt-32 flex flex-col items-start gap-32">
              <div
                className={`grid w-full grid-cols-2 gap-0 sm:grid-cols-4 ${gridStyles['unique-org-grid']}`}
              >
                {orgCharts.map((company) => (
                  <OrgChartsGridItem key={company.id} company={company} />
                ))}
              </div>
              <a
                className="font-suisse-intl text-body-lg underline transition-colors duration-150 hover:text-primary-magenta"
                href={org_charts_path()}
                onClickCapture={handleViewAllClick}
              >
                {VIEW_ALL_ORG_CHARTS}
              </a>
            </div>
          ) : (
            <div className="grid w-full place-items-center">
              <Spinner marginY="small" />
            </div>
          )}
        </InView>
      </Track>
    </ForYouSection>
  );
};

export default ForYouOrgCharts;
